import { Alert, Button, Divider, Dropdown, Menu, message, Space, Typography } from "antd";
import React, { useState } from "react";
import { SendOutlined, MoreOutlined } from "@ant-design/icons";
import conditional from "../components/utilities/conditional";
import { useSession } from "../util/Session";
import { apiCall } from "../components/utilities/Api";
import { STEPS } from "./NewPurchaseLayout";

export default function PurchaseFulfillmentSelector(props) {

    const {
        status,
        callback = () => { }
    } = props;

    const [session, setSession] = useSession();
    const [loading, setLoading] = useState("");

    const checkoutInfo = window.sitesettings?.checkout_info || {};
    const storeInfo = window.sitesettings?.store_info || {};
    const cart = session?.cart || {};
    const summary = cart?.summary || {};

    const containsUnavailable = !!cart.contains_unavailable;
    const containsUnshippable = !!cart.contains_unshippable;
    const electronicOnly = !!summary.contains_only_emailed_gift_card;

    const guestCheckoutAllowed =
        session?.logged_in ||
        window.sitesettings?.guest_allow_checkout === true ||
        checkoutInfo.guest_allow_checkout === true;

    const shipEnabled = checkoutInfo.ship_enabled === true;
    const pickupEnabled = checkoutInfo.pickup_enabled === true;

    /*
     * Support either setting name.
     * Remove the fallback that your site does not use.
     */
    const localDeliveryEnabled =
        checkoutInfo.local_delivery_enabled === true ||
        checkoutInfo.delivery_enabled === true;

    const customShipEnabled =
        checkoutInfo.active_custom_ship === true &&
        shipEnabled;

    const customPickupEnabled =
        checkoutInfo.active_custom_pickup === true &&
        pickupEnabled &&
        guestCheckoutAllowed;

    const selectDelivery = (
        deliveryPreference,
        custom = false,
        label = ""
    ) => {
        const loadingKey = `${deliveryPreference}:${custom}`;

        setLoading(loadingKey);

        apiCall(
            "checkout/setDelivery",
            {
                delivery_preference: deliveryPreference,
                custom
            },
            (_status, _result) => {
                setLoading("");

                if (_status) {
                    setSession(prev => ({
                        ...prev,
                        cart: _result
                    }));

                    callback({
                        delivery_preference: deliveryPreference,
                        custom,
                        cart: _result,
                        label : label
                    });

                    return;
                }

                message.error(
                    _result?.error || "Unable to select delivery method."
                );
            }
        );
    };

    const isLoading = (deliveryPreference, custom = false) => {
        return loading === `${deliveryPreference}:${custom}`;
    };

    const getPickupText = () => {
        if (
            storeInfo.is_multistore &&
            storeInfo.multistore_info?.length
        ) {
            const location =
                storeInfo.multistore_info.find(
                    item => item.id === session?.location_id
                ) ||
                storeInfo.multistore_info[0];

            if (location?.name) {
                return `Pick up at ${location.name}`;
            }
        }

        return "Pick Up In Store";
    };

    const hasGiftRegistryShippingRecommendation = session?.logged_in && session?.user?.alt_addresses?.some(item => item.shipping_encouraged);
    const shipDisabled = containsUnavailable || containsUnshippable || !shipEnabled;
    const localDeliveryDisabled = containsUnavailable || containsUnshippable || !localDeliveryEnabled;
    const pickupDisabled = containsUnavailable || !pickupEnabled || !guestCheckoutAllowed;



    if (status?.step === STEPS.EMAIL) {
        return (<></>);
    }
    if (status.step !== STEPS.FULFILLMENT) {
        return (<>
            <div className="shim" /><div className="shim" />
            <div className="checkout-row">
                <div className="checkout-info">
                    <div className="checkout-icon">
                        <SendOutlined />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }} > Delivery method </Typography.Text>
                        <Typography.Text strong ellipsis style={{ display: "block", maxWidth: 400 }} > {status?.delivery_preference_label || ""} </Typography.Text>
                    </div>
                </div>
                <Button type="text" onClick={() => props.changeFulfillment()} ><small>change</small></Button>
            </div>

        </>)
    }


    return (
        <>


            <Divider orientation="left" orientationMargin={0}><big><strong>Select delivery method</strong></big></Divider>

            <conditional.true
                value={hasGiftRegistryShippingRecommendation}
            >
                <Alert
                    style={{
                        textAlign: "center",
                        marginBottom: 15
                    }}
                    message={
                        <>
                            <span style={{ fontWeight: 600 }}>
                                We recommend you choose the{" "}
                                <u>Ship</u> option.
                            </span>{" "}
                            Your cart contains items from a Gift Registry.
                            The registry creator would prefer those items
                            shipped to an address of their choosing.
                        </>
                    }
                    type="warning"
                    showIcon
                />
            </conditional.true>

            {electronicOnly ? (
                <Button
                    block
                    type="primary"
                    size="large"
                    disabled={containsUnavailable}
                    loading={isLoading("electronic")}
                    onClick={() => selectDelivery("electronic", false, "Email")}
                >
                    Email
                </Button>
            ) : (
                <Space
                    direction="vertical"
                    size={10}
                    style={{ width: "100%" }}
                >
                    {shipEnabled && (
                        <Button
                            size="large"
                            block
                            disabled={shipDisabled}
                            loading={isLoading("ship")}
                            onClick={() => selectDelivery("ship", false, "Ship")}
                        >
                            Ship
                        </Button>
                    )}



                    {pickupEnabled && guestCheckoutAllowed && (
                        <Button
                            block
                            size="large"
                            disabled={pickupDisabled}
                            loading={isLoading("pickup")}
                            onClick={() => selectDelivery("pickup", false, getPickupText())}
                        >
                            {getPickupText()}
                        </Button>
                    )}

                    {customShipEnabled && (
                        <Button
                            block
                            size="large"
                            disabled={shipDisabled}
                            loading={isLoading("ship", true)}
                            onClick={() => {
                                selectDelivery("ship", true, checkoutInfo.label_custom_ship || "Custom Shipping");
                            }}
                        >
                            {checkoutInfo.label_custom_ship ||
                                "Custom Shipping"}
                        </Button>
                    )}

                    {customPickupEnabled && (
                        <Button
                            block
                            size="large"
                            disabled={pickupDisabled}
                            loading={isLoading("pickup", true)}
                            onClick={() => {
                                selectDelivery("pickup", true, checkoutInfo.label_custom_pickup || "Custom Pickup");
                            }}
                        >
                            {checkoutInfo.label_custom_pickup ||
                                "Custom Pickup"}
                        </Button>
                    )}
                </Space>
            )}

            {!electronicOnly &&
                !shipEnabled &&
                !localDeliveryEnabled &&
                !(pickupEnabled && guestCheckoutAllowed) && (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 15 }}
                        message="No fulfillment methods are currently available."
                    />
                )}
        </>
    );
}